import cx from 'classnames';
import { Button } from '@ui/components/ui/button';
import {
  DISPUTED_STATUS,
  reportStatusesOf,
  ApprovalPopup,
  ClearFilterButton,
  DateRangeFilter,
  DisputePopup,
  FilterField,
  Pagination,
  SprintAction,
  SprintSection,
  PastSprintsDivider,
  ShowLastSprintCheckbox,
  startsPastSprints,
  UnreadCommentDot,
  hasDispute,
  hasUnreadComment,
  sprintActionsFor,
} from '@app/components/core';
import { productReportingPath, productsReportingPath } from '@app/constants/url';
import { filterBarClass, pageHeaderRowClass, pageTitleClass } from '@app/constants/styles';
import { capitalize, shortDate, toFixedHours } from '@app/utils';
import { ProductsReportingNavState } from '@app/utils/types';
import {
  ApproveSprintFormValues,
  DisputeSprintFormValues,
  ProductEntity,
  ProductStatus,
  SprintProductEntity,
  SprintProductStatus,
  useApproveSprintProduct,
  useCreateSprintProduct,
  useDismissDispute,
  useDisputeSprintProduct,
  usePmProductsBySprint,
  useListProducts,
  useListSprintProductClients,
  useSendSprintProduct,
  useUpdateSprintProduct,
} from '@data';
import { useOrganizationChapters } from '@data/hooks/organization';
import { useMarkAllCommentsRead, useUnreadCommentCount } from '@data/hooks/sprintProducts';
import React, { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationId, useSectionToggle, useSprintPaging } from '@app/utils/hooks';
import { useLocation } from 'react-router-dom';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { Loader } from '@ui';
import {
  ArchiveRestore,
  ChevronRight,
  CircleCheck,
  MessageSquare,
  MessageSquareOff,
  Package,
  Send,
  ShoppingCart,
} from 'lucide-react';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { Checkbox } from '@ui/components/ui/checkbox';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable, DataTableColumnHeader } from '@ui/components/ui/data-table';
import { SprintEntity } from '@data/utils/types';
import { clientPickerOptions, matchesClientFilter } from '../../products/productsList/productRows';
import SendSprintPopup from '../sendSprint/SendSprintPopup';
import CloseProductConfirm from '../CloseProductConfirm';
import ConfirmDialog from '../../ConfirmDialog';

/** Which decision a row was clicked for. Dismiss is a plain confirm; the other two open their popup. */
type DecisionKind = 'approve' | 'dispute' | 'dismiss';

type CloseTarget = {
  productId: string;
  productName: string;
  sprintId: string;
  sprint: SprintEntity;
  sprintProduct?: SprintProductEntity;
};

type ProductsReportingRow = Omit<ProductEntity, 'chapters' | 'status'> & {
  chapters: string;
  hours: number;
  status: SprintProductStatus;
  sprintId: string;
  // The row's sprint travels with the data (not via a captured closure) so the close handler always
  // sees the right sprint even though the columns are memoised before the sprint list has loaded.
  sprint: SprintEntity;
};

function ProductsReportingListLoaded({ organizationId }: { organizationId: string }): ReactElement {
  const location = useLocation();
  const transitionNavigate = useViewTransitionNav();
  const routeState = location.state as ProductsReportingNavState | undefined;
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [showAllSprints, setShowAllSprints] = useSectionToggle('reporting-all-sprints', [
    productsReportingPath(organizationId),
  ]);

  // includeDisabled: render labels for historical involvements pointing at chapters the admin
  // has since disabled. The picker (ReportModal) stays on the active-only default.
  const { data: chapters } = useOrganizationChapters(organizationId, { includeDisabled: true });

  const {
    sprints,
    sprintsArePrevious,
    baseSprint,
    listSprints,
    yearPages,
    pageCount,
    currentPage,
    setPage,
    fromMissing,
    toMissing,
    currentSprint,
    showsAllSprints,
  } = useSprintPaging({ from, to, showAllSprints });

  const unreadFilterOn = unreadOnly;

  const { data: unreadComments } = useUnreadCommentCount(organizationId);
  const { mutate: markAllCommentsRead, isPending: isMarkingRead } = useMarkAllCommentsRead(organizationId);
  const [confirmingMarkAllRead, setConfirmingMarkAllRead] = useState(false);
  // "What have I not read" is not a question about one sprint, so while the filter is on the list
  // stops laying out the calendar and lists only the sprints holding something unread — newest first,
  // whatever year they are in. Without this the year is laid out in full and every section of it comes
  // back empty, and a comment in an earlier year is not on the page at all.
  const unreadSprints = useMemo(() => {
    const ids = new Set(unreadComments?.sprintIds ?? []);
    return (sprints?.items ?? [])
      .filter(sprint => ids.has(sprint.id))
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [sprints, unreadComments]);

  // Named apart from the hook's own `listSprints`, which is the calendar's answer to the same question.
  const sectionSprints = unreadFilterOn ? unreadSprints : listSprints;
  const sectionBaseSprint = unreadFilterOn ? unreadSprints[0] : baseSprint;

  const productPmParams = {
    from: sectionBaseSprint?.startDate,
    to: sectionBaseSprint?.endDate,
    sprintNumber: sectionBaseSprint?.number,
    sprintId: sectionBaseSprint?.id,
  };

  // The sprint window rides on each sprint's own query, so these carry only the list-wide filters.
  // `unreadOnly` is one of those rather than part of the window: it narrows which products a sprint
  // answers with, and it has to reach every sprint's query, not just the one the mutations key off.
  const productPmFilters = useMemo(
    () => ({ statuses: undefined, search: undefined, ...(unreadFilterOn ? { unreadOnly: true } : {}) }),
    [unreadFilterOn],
  );

  const {
    pages: productPages,
    ask,
    isFetching,
  } = usePmProductsBySprint(organizationId, sectionSprints, productPmFilters, !!sectionBaseSprint);

  const { data: products } = useListProducts(organizationId, {
    statuses: [ProductStatus.active, ProductStatus.support],
  });

  const { mutate: createSprintProduct } = useCreateSprintProduct(organizationId, productPmParams);
  const { mutate: updateSprintProduct } = useUpdateSprintProduct(organizationId, productPmParams);

  const { t } = useTranslation();

  // Small outlined buttons rather than underlined words: the pair sits in the middle of a sentence, and
  // an edge is what stops them reading as text that happens to be clickable.
  //
  // Clearing asks first, the way removing a member does. Undoing it is one press of the toast, but the
  // question is worth asking anyway: the queue is the only record of what is left to read, and someone
  // who meant to press Show has nothing on screen afterwards to tell them what they just skipped.
  const markAllReadButton = (
    <Button variant="outline" size="sm" onClick={() => setConfirmingMarkAllRead(true)} loading={isMarkingRead}>
      {t('actions.markAllRead')}
    </Button>
  );

  const [sendModalSprintProduct, setSendModalOpen] = useState<SprintProductEntity | undefined>(undefined);
  const { data: sprintProductClients } = useListSprintProductClients(sendModalSprintProduct?.id);
  const { mutate: sendSprint } = useSendSprintProduct();

  const [statusFilter, setStatusFilter] = useState<string[]>(routeState?.statusFilter || []);
  const [productsFilter, setProductsFilter] = useState<string[]>(routeState?.productsFilter || []);
  const [clientFilter, setClientFilter] = useState<string[]>(routeState?.clientFilter || []);

  const filtersActive =
    clientFilter.length > 0 ||
    productsFilter.length > 0 ||
    statusFilter.length > 0 ||
    Boolean(from || to) ||
    unreadFilterOn;
  const clearFilters = (): void => {
    setClientFilter([]);
    setProductsFilter([]);
    setStatusFilter([]);
    setFrom('');
    setTo('');
    setUnreadOnly(false);
    setShowAllSprints(false);
  };

  // Product the user is closing, held while we check whether its team still has days left to report.
  const [closeTarget, setCloseTarget] = useState<CloseTarget | undefined>(undefined);

  // The report being decided on from a row, and which decision. The row already carries the report, so
  // unlike a product's Sprints tab there is nothing to fetch first — the popups take it as it stands.
  const [deciding, setDeciding] = useState<
    { sprintProduct: SprintProductEntity; productId: string; sprintId: string; kind: DecisionKind } | undefined
  >(undefined);
  const { mutate: approveSprintProduct, isPending: isApproving } = useApproveSprintProduct();
  const { mutate: disputeSprintProduct, isPending: isDisputing } = useDisputeSprintProduct();
  const { mutate: dismissDispute } = useDismissDispute();

  // Approving from a row goes through the approve endpoint, the same as approving from the report itself:
  // it records who decided, mails everyone on the list and settles the remark it answers. It used to be a
  // bare status update from here, which did none of those.
  const handleApprove = (values: ApproveSprintFormValues): void =>
    approveSprintProduct(values, { onSuccess: () => setDeciding(undefined) });

  const handleDispute = (values: DisputeSprintFormValues): void =>
    disputeSprintProduct(values, { onSuccess: () => setDeciding(undefined) });

  const handleDismissDispute = (): void => {
    if (!deciding) return;
    dismissDispute(
      { sprintProductId: deciding.sprintProduct.id, sprintId: deciding.sprintId, productId: deciding.productId },
      { onSuccess: () => setDeciding(undefined) },
    );
  };

  const closeProduct = ({ productId, sprintId, sprintProduct }: CloseTarget): void =>
    !sprintProduct
      ? createSprintProduct({ status: SprintProductStatus.closed, sprintId, productId })
      : updateSprintProduct({ id: sprintProduct.id, status: SprintProductStatus.closed, sprintId, productId });

  const renderActions = ({ sprintProducts, id: productId, name, sprint }: ProductsReportingRow, sprintId?: string) => {
    const sprintProduct = sprintProducts?.[0];
    const updateStatus = (status: SprintProductStatus) =>
      updateSprintProduct({ id: sprintProduct?.id, status, sprintId, productId });

    const reopenButton = (
      <Button size="sm" variant="ghost" key="reopen" onClick={() => updateStatus(SprintProductStatus.open)}>
        <ArchiveRestore />
        {t('actions.reopen')}
      </Button>
    );

    const closeButton = (
      <Button
        size="sm"
        variant="ghost"
        key="close"
        onClick={() =>
          setCloseTarget({
            productId,
            productName: capitalize(name) ?? name,
            sprintId: sprintId ?? sprint.id,
            sprint,
            sprintProduct,
          })
        }
      >
        <Package />
        {t('actions.close')}
      </Button>
    );

    const decide = (kind: DecisionKind) => (): void => {
      if (!sprintProduct) return;
      setDeciding({ sprintProduct, productId, sprintId: sprintId ?? sprint.id, kind });
    };

    const approveButton = (
      <Button size="sm" variant="ghost" key="approve" onClick={decide('approve')}>
        <CircleCheck />
        {t('actions.approve')}
      </Button>
    );

    const disputeButton = (
      <Button size="sm" variant="ghost" key="dispute" onClick={decide('dispute')}>
        <MessageSquare />
        {t('actions.dispute')}
      </Button>
    );

    const dismissDisputeButton = (
      <Button size="sm" variant="ghost" key="dismissDispute" onClick={decide('dismiss')}>
        <MessageSquareOff />
        {t('actions.dismissDispute')}
      </Button>
    );

    const sendButton = (
      <Button size="sm" variant="ghost" key="send" onClick={() => setSendModalOpen(sprintProduct)}>
        <Send />
        {`${t('actions.send')} (${sprintProduct?.clientCount || 0})`}
      </Button>
    );

    const invoiceButton = (
      <Button size="sm" variant="ghost" key="invoice" onClick={() => updateStatus(SprintProductStatus.invoiced)}>
        <ShoppingCart />
        {t('actions.invoice')}
      </Button>
    );

    // What the report offers is `sprintActionsFor`'s call, shared with the report's own header and with a
    // product's Sprints tab. This map only says what each one looks like in a row.
    const buttonFor: Record<SprintAction, ReactElement> = {
      [SprintAction.close]: closeButton,
      // A row does not distinguish the two closes: it has no summary on it, so "with changes" would be
      // telling the reader something they cannot see from here.
      [SprintAction.closeAnswer]: closeButton,
      [SprintAction.reopen]: reopenButton,
      [SprintAction.dispute]: disputeButton,
      [SprintAction.approve]: approveButton,
      [SprintAction.dismissDispute]: dismissDisputeButton,
      [SprintAction.send]: sendButton,
      [SprintAction.invoice]: invoiceButton,
    };

    return sprintActionsFor(sprintProduct).map(action => buttonFor[action]);
  };

  // Everything a report can read as, which is not the same as everything it can be stored as: a
  // disputed report is `closed` underneath and the table flags it Disputed. Offering only the stored
  // statuses left the one state a reader is most likely to filter for out of the list entirely.
  const statusFilterOptions = useMemo(
    () =>
      [...Object.values(SprintProductStatus), DISPUTED_STATUS]
        .sort((a, b) => a.localeCompare(b))
        .map(status => ({
          value: status,
          label: status === DISPUTED_STATUS ? t('actions.disputed') : (capitalize(status) ?? status),
        })),
    [t],
  );

  const productOptions = (products?.items || []).map(product => ({
    value: product.id,
    label: capitalize(product.name) ?? product.name,
  }));

  // From the sprints loaded so far, since those rows are the ones the filter narrows.
  const clientOptions = clientPickerOptions(
    productPages.flatMap(page => page.items ?? []),
    clientFilter,
    t('noClient'),
  );

  const productFilterFunction = (product: ProductEntity): boolean => {
    const sprintProduct = product.sprintProducts?.[0];
    // A product with no sprint product yet is effectively "open".
    // Asked through the same rule the pills render from, so a row cannot read as one thing here and be
    // filtered as another.
    const statuses = reportStatusesOf(sprintProduct);
    const statusCondition = !statusFilter.length || statuses.some(status => statusFilter.includes(status));
    const productCondition = !productsFilter.length || productsFilter.includes(product.id);
    return statusCondition && productCondition && matchesClientFilter(product, clientFilter);
  };

  const getFilteredProducts = (items: ProductEntity[]): ProductEntity[] => items.filter(productFilterFunction);

  // How tall to draw a sprint that has not arrived yet, in rows as the table draws them: products the
  // filters leave out are not rows, and it is the rows that decide the height. Read straight rather
  // than memoised — `getFilteredProducts` closes over the status and product filters, and a memo that
  // did not list them among its dependencies kept sizing placeholders for the view before the filter
  // was applied.
  //
  // The guess used before the product list itself has arrived is not visual polish. Section height is
  // what decides how many sections fall inside the near-viewport margin on first paint, and each of
  // those asks the server for its own rows — so this number sets how many requests leave at once.
  // Guessing five drew every sprint barely taller than its heading, at exactly the moment the section
  // count was being decided, and a dozen of them landed in the window together. A full section's
  // worth is the honest guess, and twelve is the size the cap below already calls typical.
  const loadedRows = productPages.find(page => page.items)?.items;
  const placeholderRows =
    (loadedRows ? getFilteredProducts(loadedRows).length : 0) || Math.min(products?.items?.length ?? 12, 12);

  // Nothing is said about the whole list until every sprint in it has answered. Judging on the ones
  // that had replied so far would put "no unread comments" above skeletons still filling in, which
  // the next reply could contradict. A sprint that failed has not answered either: it keeps its
  // section and its retry, and the list stays silent rather than counting it as empty.
  const everyPageAnswered = productPages.length > 0 && productPages.every(page => page.items);

  // Two different empty results, and only the server can tell them apart. Asking `getFilteredProducts`
  // would fold the product and status pickers in, so a status filter hiding the only unread report
  // would have the page report there was nothing to read.
  const noUnreadFromServer = everyPageAnswered && productPages.every(page => !(page.items ?? []).length);
  // Every sprint on the page was dropped, whichever filter did it.
  const everythingFilteredOut =
    everyPageAnswered && productPages.every(page => !getFilteredProducts(page.items ?? []).length);

  // Which of the three things to say when the page comes back empty. The unread filter has two of its
  // own, because only the server can tell "nothing is unread" from "the pickers hid what was".
  let emptyMessage = 'noEntriesFound';
  if (unreadFilterOn) emptyMessage = noUnreadFromServer ? 'noUnreadComments' : 'unreadHiddenByFilters';

  const getRowData = (products: ProductEntity[], sprint: SprintEntity): ProductsReportingRow[] =>
    products.map(product => {
      const status = product.sprintProducts?.[0]?.status || SprintProductStatus.open;

      const hours = product.totalMinutes / 60;

      return {
        ...product,
        chapters: product.chapters.map(chapter => chapters?.items?.find(c => c.role === chapter)?.text).join(', '),
        hours: toFixedHours(hours),
        status,
        sprintId: sprint.id,
        sprint,
      };
    });

  /* eslint-disable react/no-unstable-nested-components -- TanStack Table column render props */
  const columns = useMemo<ColumnDef<ProductsReportingRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('titles.products')} />,
        // The dot leads the name, the way an unreported day is marked on the Reporting page.
        cell: ({ row }) => (
          <span className="flex items-center gap-2 text-sm font-medium text-foreground">
            {hasUnreadComment(row.original.sprintProducts?.[0]) && <UnreadCommentDot />}
            {capitalize(row.original.name)}
          </span>
        ),
      },
      {
        accessorKey: 'hours',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('totalHours')} />,
      },
      {
        accessorKey: 'status',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('status')} />,
        cell: ({ row }) => {
          // A disputed report is `closed` underneath, and a row reading "Closed" next to a Dismiss dispute
          // button contradicts itself. Flagged the way the report's own header flags it.
          const disputed = hasDispute(row.original.sprintProducts?.[0]);
          return (
            <span
              className={cx(
                'inline-flex rounded-md px-2 py-0.5 text-xs font-medium capitalize',
                disputed ? 'bg-destructive/10 text-destructive' : 'bg-muted text-foreground',
              )}
            >
              {disputed ? t('actions.disputed') : capitalize(row.original.status)}
            </span>
          );
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        header: '',
        cell: ({ row }) => (
          <div className="flex min-w-[18rem]">
            {/* Only the buttons swallow the click; the surrounding cell space still navigates the row. */}
            <div className="flex flex-wrap gap-1" role="presentation" onClick={e => e.stopPropagation()}>
              {renderActions(row.original, row.original.sprintId)}
            </div>
          </div>
        ),
      },
      {
        id: 'go',
        enableSorting: false,
        header: '',
        meta: { className: 'w-0 text-right' },
        cell: () => <ChevronRight className="size-4 text-muted-foreground" />,
      },
    ],
    [t, organizationId, clientFilter, productsFilter, statusFilter],
  );
  /* eslint-enable react/no-unstable-nested-components */

  // Three distinct render states so we never show a perpetual loader:
  // - sprints undefined        -> still fetching the sprint list, contained loader.
  // - sprints loaded but empty  -> no sprint matches the active tab's window (e.g. the past
  //                               tab when no sprint precedes the current one), empty state.
  // - sprints + sectionBaseSprint OK -> the sections render.
  // Loader is non-absolute so it sits inline instead of escaping over the page header.
  let listContent: ReactElement;
  // While the filter is on, which sprints to show is an answer we are still waiting for, so an empty
  // set means "not yet" until it arrives rather than "nothing". `sprintsArePrevious` matters here and
  // nowhere else on this page: the sprint list is deliberately kept from the tab we left while the new
  // one loads, and reading unread sprint ids against that list finds none of them — which looked
  // exactly like having nothing unread, for as long as the past sprints took to arrive.
  if (!sprints || (unreadFilterOn && (!unreadComments || sprintsArePrevious))) {
    listContent = (
      <div className="py-10">
        <Loader absolute={false} />
      </div>
    );
  } else if (!sectionBaseSprint) {
    // Under the filter there is nothing unread to show, which is not the same as this view having
    // no sprints in it — saying the latter would send the reader looking for a sprint that is there.
    listContent = (
      <p className="py-10 text-center text-sm text-muted-foreground">
        {t(unreadFilterOn ? 'noUnreadComments' : 'noSprintForTab')}
      </p>
    );
  } else {
    listContent = (
      // Fills the page frame, so a short year sends its pager to the foot rather than under the last row.
      <div className="flex flex-1 flex-col">
        {/* Keyed on the year page: every sprint of it is laid out at once, so the fade plays for the
            page rather than for whichever rows happened to arrive first. */}
        <div key={currentPage} className="duration-200 animate-in fade-in-0">
          {productPages
            // A sprint with nothing left in it after the filters drops out entirely, heading and all:
            // asking for one thing and being handed a column of empty cards is not an answer, it is a
            // list of the sprints you did not ask about. Only while a filter is on — unfiltered, an
            // empty sprint is a fact about that sprint and belongs on the page. One still on its way
            // stays either way, or it would never come near the viewport to be asked for.
            .filter(page => !(filtersActive && page.items && !getFilteredProducts(page.items).length))
            .map((page, index, shown) => (
              <React.Fragment key={page.sprint.id}>
                {startsPastSprints(shown, index, currentSprint?.id) && <PastSprintsDivider />}
                <SprintSection
                  title={`Sprint ${page.sprint.number} (${shortDate(page.sprint.startDate)} – ${shortDate(
                    page.sprint.endDate,
                  )})`}
                  columnCount={columns.length}
                  rowCount={placeholderRows}
                  isLoaded={!!page.items}
                  hasFailed={page.hasFailed}
                  onRetry={page.retry}
                  onNear={() => ask(page.sprint.id)}
                >
                  <DataTable<ProductsReportingRow>
                    columns={columns}
                    data={getRowData(getFilteredProducts(page.items ?? []), page.sprint)}
                    padded
                    initialSorting={[{ id: 'name', desc: false }]}
                    getRowId={row => `${row.sprintId}-${row.id}`}
                    onRowClick={row =>
                      transitionNavigate(productReportingPath(organizationId, row.id, row.sprintId), 'forward', {
                        state: { clientFilter, productsFilter, statusFilter },
                      })
                    }
                  />
                </SprintSection>
              </React.Fragment>
            ))}
        </div>
        {/* Every sprint that came back was dropped above, so say why once rather than leave a blank.
            The unread filter has its own two answers, because only the server can tell "nothing is
            unread" from "the pickers hid it"; every other filter has the one. */}
        {filtersActive && !isFetching && everythingFilteredOut && (
          <p className="py-10 text-center text-sm text-muted-foreground">{t(emptyMessage)}</p>
        )}
        {/* The unread filter already shows every year at once, and hidden past sprints leave nothing to page. */}
        {!unreadFilterOn && showsAllSprints && (
          <Pagination
            className="mt-auto pt-8"
            page={currentPage}
            pageCount={pageCount}
            onChange={setPage}
            labels={yearPages.map(yearPage => String(yearPage.year))}
            reversed
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-1 flex-col">
      <div className={`${pageHeaderRowClass} mb-5`}>
        <h1 className={pageTitleClass}>{t('titles.reporting')}</h1>
      </div>

      {/* The one place the queue is stated, and the only place it can be acted on. It follows the reader
          across the tabs rather than being repeated among the filters: the comments sit on approved
          reports, so the tab you land on can never hold them, and the tab that can is one press away. */}
      {!!unreadComments?.count && (
        <div className="mb-6 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span className="size-2 shrink-0 rounded-full bg-destructive" aria-hidden />
          <span>{t('unreadCommentsElsewhere', { count: unreadComments.count })}</span>
          <span className="flex flex-wrap items-center gap-2">
            {/* Nothing to offer once they are on screen. */}
            {!unreadFilterOn && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // A leftover range would narrow the sprints being jumped to, landing Show on an empty list.
                  setFrom('');
                  setTo('');
                  setUnreadOnly(true);
                }}
              >
                {t('actions.show')}
              </Button>
            )}
            {markAllReadButton}
          </span>
        </div>
      )}

      <div className={`mb-6 ${filterBarClass}`}>
        <FilterField>
          <MultiSelectField
            label={t('client')}
            options={clientOptions}
            value={clientFilter}
            onChange={setClientFilter}
            emptyLabel={t('allClients')}
            searchPlaceholder={t('search')}
            emptySearchLabel={t('noMatches')}
            renderCountSummary={count => t('nItemsSelected', { count })}
          />
        </FilterField>
        <FilterField>
          <MultiSelectField
            label={t('titles.products')}
            options={productOptions}
            value={productsFilter}
            onChange={setProductsFilter}
            emptyLabel={t('allProducts')}
            searchPlaceholder={t('search')}
            emptySearchLabel={t('noMatches')}
            renderCountSummary={count => t('nItemsSelected', { count })}
          />
        </FilterField>
        <FilterField>
          <MultiSelectField
            id="reporting-status-filter"
            label={t('status')}
            options={statusFilterOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            emptyLabel={t('allStatuses')}
            searchPlaceholder={t('search')}
            emptySearchLabel={t('noMatches')}
            renderCountSummary={count => t('nItemsSelected', { count })}
          />
        </FilterField>
        <DateRangeFilter
          idPrefix="products"
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          fromMissing={fromMissing}
          toMissing={toMissing}
          hideClear
        />
        {/* A checkbox rather than a fifth dropdown: it answers yes/no, and the row has no width to spare. */}
        <div className="flex h-8 shrink-0 items-center gap-2">
          <Checkbox
            id="reporting-unread"
            checked={unreadOnly}
            onCheckedChange={value => setUnreadOnly(value === true)}
          />
          <label htmlFor="reporting-unread" className="cursor-pointer text-sm font-medium text-foreground">
            {t('unreadCommentsOnly')}
          </label>
        </div>
        <ShowLastSprintCheckbox
          id="reporting-all-sprints"
          showAll={showAllSprints}
          forced={Boolean(from && to) || unreadFilterOn}
          onShowAllChange={setShowAllSprints}
        />
        {filtersActive && <ClearFilterButton onClick={clearFilters} />}
      </div>

      {listContent}
      {sendModalSprintProduct && (
        <SendSprintPopup
          handleClick={() => setSendModalOpen(undefined)}
          sendSprint={sendSprint}
          sprintProduct={sendModalSprintProduct}
          sprintProductClients={sprintProductClients?.items}
        />
      )}
      {closeTarget && (
        <CloseProductConfirm
          productId={closeTarget.productId}
          productName={closeTarget.productName}
          sprint={closeTarget.sprint}
          onCancel={() => setCloseTarget(undefined)}
          onConfirm={() => {
            closeProduct(closeTarget);
            setCloseTarget(undefined);
          }}
        />
      )}

      {/* The report's own popups, so a decision made from a row is the same decision, recorded and
          forwarded the same way, as one made from the report. */}
      {deciding?.kind === 'approve' && (
        <ApprovalPopup
          approveSprintProduct={handleApprove}
          sprintProduct={deciding.sprintProduct}
          productId={deciding.productId}
          sprintId={deciding.sprintId}
          onClose={() => setDeciding(undefined)}
          isApproving={isApproving}
        />
      )}
      {deciding?.kind === 'dispute' && (
        <DisputePopup
          disputeSprintProduct={handleDispute}
          sprintProduct={deciding.sprintProduct}
          productId={deciding.productId}
          sprintId={deciding.sprintId}
          onClose={() => setDeciding(undefined)}
          isDisputing={isDisputing}
        />
      )}
      <ConfirmDialog
        open={deciding?.kind === 'dismiss'}
        onOpenChange={open => !open && setDeciding(undefined)}
        title={t('dismissDisputeTitle')}
        description={t('dismissDisputeDescription')}
        confirmLabel={t('actions.dismissDispute')}
        onConfirm={handleDismissDispute}
      />

      {/* The same question, and the same shape of answer, as removing a member. */}
      <ConfirmDialog
        open={confirmingMarkAllRead}
        onOpenChange={setConfirmingMarkAllRead}
        title={t('markAllReadTitle', { count: unreadComments?.count ?? 0 })}
        description={t('markAllReadDescription')}
        confirmLabel={t('actions.markAllRead')}
        // Not red: nothing is taken away, the comments stay where they are, and the toast undoes it.
        confirmVariant="default"
        onConfirm={() => markAllCommentsRead()}
      />
    </div>
  );
}

const ProductsReportingList = (): ReactElement => {
  const organizationId = useOrganizationId();
  if (!organizationId) {
    return <Loader />;
  }
  return <ProductsReportingListLoaded organizationId={organizationId} />;
};

export default ProductsReportingList;
